/**
 * EnterpriseRefactorIntegration - Integration Helper
 * Provides easy integration and testing utilities for the enterprise refactor
 * Ensures smooth deployment and validation
 */

import enterpriseRefactorValidator from '../tests/EnterpriseRefactorValidator.js';

export class EnterpriseRefactorIntegration {
    constructor() {
        this.initialized = false;
        this.integrationStatus = new Map();
        
        console.log('🔧 EnterpriseRefactorIntegration initialized');
    }

    /**
     * Initialize enterprise refactor integration
     */
    async initialize() {
        if (this.initialized) {
            console.log('🔧 Enterprise refactor already initialized');
            return;
        }
        
        console.log('🔧 Initializing enterprise refactor integration...');
        
        try {
            // Wait for all enterprise services to be available
            await this.waitForEnterpriseServices();
            
            // Initialize enterprise services
            await this.initializeEnterpriseServices();
            
            // Run integration validation AFTER Firebase is ready
            const validationReport = await this.runIntegrationValidationDelayed();
            
            // Update integration status
            this.updateIntegrationStatus(validationReport);
            
            this.initialized = true;
            console.log('✅ Enterprise refactor integration complete');
            
            return validationReport;
        } catch (error) {
            console.error('❌ Enterprise refactor integration failed:', error);
            throw error;
        }
    }

    /**
     * Wait for enterprise services to be available
     */
    async waitForEnterpriseServices() {
        const requiredServices = [
            () => window.enterpriseAuthService,
            () => window.desktopStyleManager,
            () => window.mobileStyleManager, // CSS-only mobile optimizations, SEPARATE from UnifiedMobileSystem
            () => window.enterpriseSceneManager // Presentation layer, SEPARATE from MobileDesktopRouter
        ];
        
        let attempts = 0;
        const maxAttempts = 20; // 5 seconds total
        
        while (attempts < maxAttempts) {
            const allServicesReady = requiredServices.every(service => {
                try {
                    return service() !== undefined;
                } catch (e) {
                    return false;
                }
            });
            
            if (allServicesReady) {
                console.log('✅ All enterprise services available');
                return;
            }
            
            console.log(`⏳ Waiting for enterprise services (attempt ${attempts + 1}/${maxAttempts})`);
            await new Promise(resolve => setTimeout(resolve, 250));
            attempts++;
        }
        
        throw new Error('Enterprise services initialization timeout');
    }

    /**
     * Initialize enterprise services
     */
    async initializeEnterpriseServices() {
        console.log('🔧 Initializing enterprise services...');
        
        // Initialize EnterpriseAuthService
        if (window.enterpriseAuthService && !window.enterpriseAuthService.isReady()) {
            await window.enterpriseAuthService.initialize();
            console.log('✅ EnterpriseAuthService initialized');
        }
        
        // Platform-specific style managers initialized (SEPARATE concerns)
        console.log('✅ Style managers active (separate from UnifiedMobileSystem)');
        
        // EnterpriseSceneManager initialized (SEPARATE from routing)
        console.log('✅ Scene presentation manager active (separate from MobileDesktopRouter)');
        
        console.log('🔧 Enterprise services initialization complete');
    }

    /**
     * Run integration validation with delay for Firebase readiness
     */
    async runIntegrationValidationDelayed() {
        console.log('🧪 Waiting for Firebase to be fully ready before validation...');
        
        // ENTERPRISE FIX: Wait for ALL systems including Firebase globals
        let attempts = 0;
        while (attempts < 100) { // Max 10 seconds for comprehensive validation
            const firebaseReady = window.firebase && window.auth && window.db && 
                                  typeof window.auth.signInWithEmailAndPassword === 'function';
            const businessLogicReady = window.hasSpecialAccess && window.sessionManager;
            const infrastructureReady = window.UIController && window.sceneManager && window.ZIndexManager;
            const templateSystemReady = window.templateOverlayManager && window.toastManager;
            
            if (firebaseReady && businessLogicReady && infrastructureReady && templateSystemReady) {
                console.log('🧪 ALL ENTERPRISE SYSTEMS READY - running validation...');
                console.log('🧪 Firebase ready:', firebaseReady);
                console.log('🧪 Business logic ready:', businessLogicReady);
                console.log('🧪 Infrastructure ready:', infrastructureReady);
                console.log('🧪 Template system ready:', templateSystemReady);
                break;
            }
            
            // Debug what's missing
            if (attempts % 10 === 0) {
                console.log(`🧪 Validation attempt ${attempts}/100 - Missing:`, {
                    firebase: !firebaseReady,
                    business: !businessLogicReady,
                    infrastructure: !infrastructureReady,
                    templates: !templateSystemReady
                });
            }
            
            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
        }
        
        if (attempts >= 50) {
            console.warn('🧪 Validation timeout - running anyway...');
        }
        
        const validationReport = await enterpriseRefactorValidator.runComprehensiveValidation();
        
        console.log('🧪 Integration validation complete');
        return validationReport;
    }

    /**
     * Run integration validation
     */
    async runIntegrationValidation() {
        console.log('🧪 Running integration validation...');
        
        const validationReport = await enterpriseRefactorValidator.runComprehensiveValidation();
        
        console.log('🧪 Integration validation complete');
        return validationReport;
    }

    /**
     * Update integration status
     */
    updateIntegrationStatus(validationReport) {
        this.integrationStatus.set('validationReport', validationReport);
        this.integrationStatus.set('successRate', validationReport.summary.successRate);
        this.integrationStatus.set('enterpriseGrade', validationReport.enterpriseGrade);
        this.integrationStatus.set('timestamp', new Date().toISOString());
        
        // Store in sessionStorage for persistence
        try {
            sessionStorage.setItem('enterpriseRefactorStatus', JSON.stringify({
                successRate: validationReport.summary.successRate,
                enterpriseGrade: validationReport.enterpriseGrade,
                timestamp: new Date().toISOString()
            }));
        } catch (e) {
            console.warn('Could not store integration status in sessionStorage:', e);
        }
    }

    /**
     * Get integration status
     */
    getIntegrationStatus() {
        return {
            initialized: this.initialized,
            status: Object.fromEntries(this.integrationStatus),
            services: {
                enterpriseAuthService: !!window.enterpriseAuthService,
                desktopStyleManager: !!window.desktopStyleManager,
                mobileStyleManager: !!window.mobileStyleManager,
                enterpriseSceneManager: !!window.enterpriseSceneManager,
                unifiedMobileSystem: !!window.unifiedMobile // Separate system
            }
        };
    }

    /**
     * Force enterprise mode for testing
     */
    forceEnterpriseMode() {
        console.log('🚨 Forcing enterprise mode for testing...');
        
        // Force all services to enterprise mode
        if (window.enterpriseAuthService) {
            window.enterpriseAuthService.authProvider = 'professional';
        }
        
        if (window.desktopStyleManager) {
            window.desktopStyleManager.forceDesktopMode();
        }
        
        if (window.mobileStyleManager) {
            window.mobileStyleManager.forceMobileMode();
        }
        
        console.log('🚨 Enterprise mode forced');
    }

    /**
     * Quick health check
     */
    healthCheck() {
        const services = [
            { name: 'EnterpriseAuthService', service: window.enterpriseAuthService },
            { name: 'DesktopStyleManager', service: window.desktopStyleManager },
            { name: 'MobileStyleManager', service: window.mobileStyleManager },
            { name: 'EnterpriseSceneManager', service: window.enterpriseSceneManager },
            { name: 'UnifiedMobileSystem (Separate)', service: window.unifiedMobile }
        ];
        
        const healthStatus = services.map(({ name, service }) => ({
            name,
            status: service ? 'HEALTHY' : 'UNAVAILABLE',
            initialized: service?.isReady?.() ?? service?.isInitialized ?? 'UNKNOWN'
        }));
        
        const overallHealth = healthStatus.every(s => s.status === 'HEALTHY') ? 'HEALTHY' : 'DEGRADED';
        
        console.log('🏥 Enterprise Refactor Health Check:');
        healthStatus.forEach(({ name, status, initialized }) => {
            const icon = status === 'HEALTHY' ? '✅' : '❌';
            console.log(`  ${icon} ${name}: ${status} (${initialized})`);
        });
        console.log(`🏥 Overall Health: ${overallHealth}`);
        
        return {
            overallHealth,
            services: healthStatus,
            timestamp: new Date().toISOString()
        };
    }

    /**
     * Emergency rollback (if needed)
     */
    emergencyRollback() {
        console.warn('🚨 EMERGENCY ROLLBACK INITIATED');
        
        try {
            // Disable enterprise services
            if (window.desktopStyleManager) {
                window.desktopStyleManager.cleanup();
            }
            
            if (window.mobileStyleManager) {
                window.mobileStyleManager.cleanup();
            }
            
            if (window.enterpriseSceneManager) {
                window.enterpriseSceneManager.cleanup();
            }
            
            // UnifiedMobileSystem cleanup handled by its own system
            
            // Clear enterprise flags
            sessionStorage.removeItem('enterpriseRefactorStatus');
            
            console.warn('🚨 Emergency rollback completed - system reverted to legacy mode');
            
            return { success: true, message: 'System reverted to legacy mode' };
        } catch (error) {
            console.error('🚨 Emergency rollback failed:', error);
            return { success: false, message: error.message };
        }
    }
}

// Create singleton instance
const enterpriseRefactorIntegration = new EnterpriseRefactorIntegration();

// Make globally available
window.enterpriseRefactorIntegration = enterpriseRefactorIntegration;

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => enterpriseRefactorIntegration.initialize(), 1000);
    });
} else {
    setTimeout(() => enterpriseRefactorIntegration.initialize(), 1000);
}

export default enterpriseRefactorIntegration;
